import { z } from 'zod'

export const MergeTargetSchema = z.object({
    database: z.string(),
    table: z.string(),
    column: z.string(),
    policy: z.enum(['move', 'leave', 'block']),
    rows: z.number(),
})

export const MergeBlockerSchema = z.object({
    code: z.string(),
    message: z.string(),
    overridable: z.boolean(),
})

export const AccountSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    deleted: z.boolean(),
})

/**
 * What our own database knows about the source account's billing.
 *
 * Not everything Stripe has. Stripe holds the card and the payment history;
 * these are the pointers we keep copies of, and they are what gets written to
 * the audit trail before an account is disabled.
 *
 * Null for most accounts. A Stripe record usually only exists where somebody
 * has paid us directly, and most of our users are covered by their
 * organisation's licence rather than subscribing themselves.
 */
export const SourceBillingSchema = z.object({
    stripeCustomerId: z.string().nullable(),
    stripeCustomerIds: z.array(z.string()),
    stripeAccount: z.string().nullable(),
    subscriptionId: z.string().nullable(),
    subscriptionStatus: z.string().nullable(),
    appStoreStatus: z.string().nullable(),
    settled: z.boolean(),
})

export const OrderInProgressSchema = z.object({
    id: z.string(),
    orderStatus: z.string(),
    orderType: z.string().nullable(),
    createdAt: z.string().nullable(),
    totalAmount: z.number().nullable(),
    currency: z.string().nullable(),
    stripePaymentIntentStatus: z.string().nullable(),
    placedByAccount: z.boolean(),
})

export const MergePlanSchema = z.object({
    sourceId: z.string(),
    destinationId: z.string(),
    source: AccountSummarySchema.nullable(),
    destination: AccountSummarySchema.nullable(),
    move: z.array(MergeTargetSchema),
    leave: z.array(MergeTargetSchema),
    blocked: z.array(MergeTargetSchema),
    blockers: z.array(MergeBlockerSchema),
    ordersInProgress: z.array(OrderInProgressSchema),
    followUps: z.array(z.string()),
    billing: SourceBillingSchema.nullable(),
})

export const MergeResultSchema = z.object({
    sourceId: z.string(),
    destinationId: z.string(),
    moved: z.array(
        z.object({
            database: z.string(),
            table: z.string(),
            column: z.string(),
            rows: z.number(),
        })
    ),
    sourceDisabled: z.boolean(),
    disableError: z.string().optional(),
    /**
     * The Stripe record the old account keeps. Written to the audit table
     * before the account is disabled, and repeated here so the page can show
     * it without anyone going to look.
     */
    billing: SourceBillingSchema.nullable().optional(),
    billingRecorded: z.boolean().optional(),
    billingRecordError: z.string().optional(),
    /** True when the server ran the merge and then threw it away. */
    dryRun: z.boolean(),
})

/**
 * The destination has a Habitat Account but no Messenger Pigeon row, so there
 * is nothing for the moved rows to point at. Named because the page offers to
 * fix this one in place, which it does for no other blocker.
 */
export const DESTINATION_NOT_IN_MESSENGER_PIGEON =
    'destination_not_in_messenger_pigeon'

/** The source still has orders that have not finished one way or the other. */
export const SOURCE_HAS_ORDERS_IN_PROGRESS = 'source_has_orders_in_progress'

/**
 * An order waiting on a payment that never arrived. The only unfinished state
 * the page will close off, because nothing was paid and nothing was promised.
 */
export const PENDING_PAYMENT_STATUS = 'pending-payment'

export type MergeTarget = z.infer<typeof MergeTargetSchema>
export type OrderInProgress = z.infer<typeof OrderInProgressSchema>
export type MergeBlocker = z.infer<typeof MergeBlockerSchema>
export type MergePlan = z.infer<typeof MergePlanSchema>
export type MergeResult = z.infer<typeof MergeResultSchema>
export type SourceBilling = z.infer<typeof SourceBillingSchema>

/** Total rows across a set of targets, for the one-line summaries. */
export function totalRows(targets: MergeTarget[]): number {
    return targets.reduce((running, target) => running + target.rows, 0)
}
